import type { StoresType } from 'api/Stores/types'
import { Ranges } from 'api/System/types'
import type { ChannelsType } from 'ChannelsTabs/types'
import type { ChannelSummaryType } from 'ChannelSummary/types'
import { TypeAnalysis } from 'DatePickerDialog/constants'
import type { Expanded } from 'expanded/types'
import { moment } from 'locale'
import { createSelector } from 'reselect'
import { DEFAULT_SORT_BY } from 'revenue/RevenueDetails/constants'
import { getCompareFunction } from 'revenue/RevenueDetails/selectors'
import type { State } from 'store/types'
import { emptyArray, emptyObject, parseDateForRequest } from 'utils'

import type { DetailsData, GetDateKeyOptions } from './types'
import { getDateKey } from './utils'

interface Options {
  channel?: string
  countryId?: string
  dataKey?: string
  expandType?: keyof Expanded
  owner?: string
  saleType?: string
  storeId?: number
  storesType?: StoresType
  subListTypeSelected?: ChannelsType
  type?: ChannelSummaryType
}

function filterByStores({ storesCurrentECom, storesCurrentNECom }: DetailsData) {
  return Boolean(storesCurrentECom || storesCurrentNECom)
}

export const selectChannelsType = (state: State) => state.channelsType

export const selectSubListTypeSelected = createSelector(
  selectChannelsType,
  (state: State) => state.subListType,
  (channelsType, subListType) => subListType[channelsType] as ChannelsType,
)

export const selectRequestParams = createSelector(
  (state: State) => state.datePicker.dateCurrent,
  (state: State) => state.datePicker.dateCompared,
  (state: State) => state.datePicker.range,
  (state: State) => state.datePicker.typeAnalysis,
  (state: State) => state.datePicker.range?.end,
  (dateCurrent, dateCompared, range, typeAnalysis, end) => ({
    dateCurrent: typeAnalysis === TypeAnalysis.perClosedPeriods ? end : dateCurrent,
    dateCompared,
    range: range?.range || Ranges.DAY,
  }),
)

export const selectRefetchParams = createSelector(selectChannelsType, selectRequestParams, (channelsType, requestParams) => ({
  ...requestParams,
  channelsType,
}))

export const selectTabRequestParams = createSelector(
  selectRefetchParams,
  selectSubListTypeSelected,
  (requestParams, subListTypeSelected) => ({
    ...requestParams,
    subListTypeSelected,
  }),
)

export const selectTabCountriesList = createSelector(
  selectTabRequestParams,
  (state: State) => state.countries,
  (tabRequestParams, countries) => ({
    list: countries[getDateKey(tabRequestParams)]?.data?.filter(filterByStores) || emptyArray,
    isLoading: countries[getDateKey(tabRequestParams)]?.isLoading || false,
  }),
)

export const selectCompletenessCountries = createSelector(
  selectRequestParams,
  (state: State) => state.countries,
  (requestParams, countries) => {
    const dataKey = getDateKey({ ...requestParams, channelsType: 'countries' })

    return {
      list: countries[dataKey]?.data || emptyArray,
      isLoading: countries[dataKey]?.isLoading || false,
    }
  },
)

export const selectCountriesList = ({ channel = '', owner = '', saleType = '' }: Options = emptyObject) =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.countries,
    (tabRequestParams, countries) => {
      const dataKey = getDateKey({
        ...tabRequestParams,
        owner,
        saleType,
        channel,
      })

      return {
        errorCode: countries[dataKey]?.errorCode || null,
        list: countries[dataKey]?.data?.filter(filterByStores) || emptyArray,
        isLoading: countries[dataKey]?.isLoading || false,
      }
    },
  )

export const selectChannelsList = ({ countryId = '', saleType = '' }) =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.channels,
    (tabRequestParams, channels) => {
      const channelsData = channels[getDateKey({ ...tabRequestParams, countryId, saleType })]

      return {
        errorCode: channelsData?.errorCode || null,
        list: channelsData?.data?.filter(filterByStores) || emptyArray,
        isLoading: channelsData?.isLoading || false,
      }
    },
  )

export const selectStore = ({ storeId = -1 }: Options) =>
  createSelector(
    (state: State) => state.store,
    (state: State) => state.datePicker.dateCurrent,
    (storeData, dateCurrent) => {
      const data = storeData[dateCurrent]?.[storeId]?.data || emptyObject
      const isLoading = storeData[dateCurrent]?.[storeId]?.isLoading || false

      return {
        isLoading,
        data,
      }
    },
  )

export const selectSkipRefetchSaleTypes = createSelector(
  selectTabRequestParams,
  (state: State) => state.saleTypes,
  (state: State, ownProps: GetDateKeyOptions) => ownProps,
  (tabRequestParams, saleTypes, ownProps) => {
    const dateToCompare = parseDateForRequest(moment(), true)
    const saleType = saleTypes[getDateKey({ ...ownProps, ...tabRequestParams })]
    const list = saleType?.data || emptyArray
    const differentFetchTime = saleType?.fetchTime === dateToCompare

    return list.length > 0 && differentFetchTime && !saleType?.errorCode
  },
)

export const selectSkipRefetchOwners = createSelector(
  selectTabRequestParams,
  (state: State) => state.owners,
  selectChannelsType,
  (tabRequestParams, owners) => {
    const dateToCompare = parseDateForRequest(moment(), true)
    const owner = owners[getDateKey(tabRequestParams)]
    const list = owner?.data || emptyArray
    const differentFetchTime = owner?.fetchTime === dateToCompare

    return list.length > 0 && differentFetchTime && !owner?.errorCode
  },
)

export const selectSkipRefetchStores = (options: Options) =>
  createSelector(selectStores(options), ({ data, fetchTime, errorCode }) => {
    const { storesType } = options

    if (!storesType || storesType === 'default') {
      return true
    }
    const dateToCompare = parseDateForRequest(moment(), true)
    const differentFetchTime = fetchTime === dateToCompare

    return data.length > 0 && differentFetchTime && !errorCode
  })

export const selectSkipRefetchStore = ({ storeId = -1 }: Options) =>
  createSelector(
    (state: State) => selectStore({ storeId })(state),
    store => Boolean(store.data?.details),
  )

export const selectSkipRefetchChannelsList = ({ countryId = '', saleType = '' }) =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.channels,
    (tabRequestParams, channels) => {
      const dateToCompare = parseDateForRequest(moment(), true)
      const channel = channels[getDateKey({ ...tabRequestParams, countryId, saleType })]
      const list = channel?.data || emptyArray
      const differentFetchTime = channel?.fetchTime === dateToCompare

      return list.length > 0 && differentFetchTime && !channel?.errorCode
    },
  )

export const selectSkipRefetchCountries = ({ channel = '', owner = '', saleType = '' }) =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.countries,
    (tabRequestParams, countries) => {
      const dataKey = getDateKey({
        ...tabRequestParams,
        owner,
        saleType,
        channel,
      })
      const dateToCompare = parseDateForRequest(moment(), true)
      const country = countries[dataKey]
      const list = country?.data || emptyArray
      const differentFetchTime = country?.fetchTime === dateToCompare

      return list.length > 0 && differentFetchTime && !country?.errorCode
    },
  )

export const selectSkipRefetchGroup = createSelector(
  selectRequestParams,
  (state: State) => state.group,
  (requestParams, group) => {
    const dateToCompare = parseDateForRequest(moment(), true)
    const item = group[getDateKey(requestParams, true)]
    const differentFetchTime = item?.fetchTime === dateToCompare

    return Boolean(item?.data) && differentFetchTime && !item?.errorCode
  },
)

export const selectSkipRefetchOmsOrigins = (options: Options) =>
  createSelector(
    (state: State) => selectOmsOrigins(options)(state),
    omsOrigin => {
      const dateToCompare = parseDateForRequest(moment(), true)
      const list = omsOrigin?.data || emptyArray
      const differentFetchTime = omsOrigin?.fetchTime === dateToCompare

      return list.length > 0 && differentFetchTime && !omsOrigin?.errorCode
    },
  )

export const selectSaleTypesList = ({ channel = '', countryId = '' }) =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.saleTypes,
    selectSubListTypeSelected,
    (tabRequestParams, saleTypes, subListTypeSelected) => {
      const dataKey = getDateKey({ ...tabRequestParams, channel, countryId, subListTypeSelected })

      return {
        list: saleTypes[dataKey]?.data?.filter(filterByStores) || emptyArray,
        isLoading: saleTypes[dataKey]?.isLoading || false,
      }
    },
  )

export const selectOwnersList = () =>
  createSelector(
    selectTabRequestParams,
    (state: State) => state.owners,
    (tabRequestParams, saleTypes) => ({
      list: saleTypes[getDateKey(tabRequestParams)]?.data?.filter(filterByStores) || emptyArray,
      isLoading: saleTypes[getDateKey(tabRequestParams)]?.isLoading || false,
    }),
  )

interface SelectDataOptions extends Options {
  type: ChannelSummaryType
}

interface SelectDataReturnType {
  data: DetailsData
  isLoading: boolean
  replenishmentVisibility: boolean
}

export const selectGroupData = ({ type = 'group' }: Partial<SelectDataOptions>) =>
  createSelector(
    selectChannelsType,
    selectRequestParams,
    (state: State) => state.group,
    (channelsType, requestParams, group) => {
      const dataKey = getDateKey({
        ...requestParams,
        channelsType,
        type,
      })

      return {
        isLoading: group[dataKey]?.isLoading || false,
        data: group[dataKey]?.data || emptyObject,
      }
    },
  )

export const selectData = ({ channel = '', countryId = '', owner = '', saleType = '', type }: SelectDataOptions) =>
  createSelector(
    selectChannelsType,
    selectRequestParams,
    selectSubListTypeSelected,
    (state: State) => state.countries,
    (state: State) => state.channels,
    (state: State) => state.group,
    (state: State) => state.owners,
    (state: State) => state.saleTypes,
    (state: State) => state.user.settings,
    (
      channelsType,
      requestParams,
      subListTypeSelected,
      countries,
      channels,
      group,
      owners,
      saleTypes,
      { replenishmentVisibility },
    ) => {
      let data
      let isLoading
      const dataKey = getDateKey({
        ...requestParams,
        channel,
        channelsType,
        countryId,
        saleType,
        subListTypeSelected,
        type,
      })

      switch (type) {
        case 'owners':
          data = owners[dataKey]?.data?.find(item => item.owner === owner)
          isLoading = owners[dataKey]?.isLoading
          break

        case 'saleTypes':
          data = saleTypes[dataKey]?.data?.find(item => item.saleType === saleType)
          isLoading = saleTypes[dataKey]?.isLoading
          break

        case 'group':
          data = group[dataKey]?.data
          isLoading = group[dataKey]?.isLoading
          break

        case 'channels':
          data = channels[dataKey]?.data?.find(item => item.channel === channel)
          isLoading = channels[dataKey]?.isLoading
          break

        case 'countries':
          data = countries[dataKey]?.data?.find(item => item.countryId === countryId)
          isLoading = countries[dataKey]?.isLoading
          break
      }

      return {
        isLoading: isLoading || false,
        data: data || emptyObject,
        replenishmentVisibility,
      } as SelectDataReturnType
    },
  )

export const selectOmsOrigins = ({ dataKey = '' }: Options) =>
  createSelector(
    (state: State) => state.omsOrigins,
    omsOrigins => ({
      data: omsOrigins[dataKey]?.data || emptyArray,
      errorCode: omsOrigins[dataKey]?.errorCode || null,
      fetchTime: omsOrigins[dataKey]?.fetchTime || '',
      isLoading: omsOrigins[dataKey]?.isLoading || false,
    }),
  )

export const selectHasOmsOrigins = ({ dataKey = '' }: Options) =>
  createSelector(
    (state: State) => state.omsOrigins,
    omsOrigins => omsOrigins[dataKey]?.data?.length > 0 || false,
  )

const defaultStores = {
  data: emptyArray,
  fetchTime: '',
  errorCode: false,
  isLoading: false,
  sortBy: DEFAULT_SORT_BY.value,
  sortOrder: DEFAULT_SORT_BY.order,
}

export const selectStores = ({ channel = '', storesType, countryId = '', owner = '', saleType = '', type }: Options) =>
  createSelector(
    selectTabRequestParams,
    selectSubListTypeSelected,
    (state: State) => state.stores,
    (state: State) => state.isListDetailsOpen,
    (tabRequestParams, subListTypeSelected, storesData, isListDetailsOpen) => {
      if (!storesType || storesType === 'default') {
        return defaultStores
      }

      const dataKey = getDateKey({
        ...tabRequestParams,
        channel,
        countryId,
        isListDetailsOpen,
        owner,
        saleType,
        storesType,
        subListTypeSelected,
        type,
      })
      const stores = storesData[dataKey]
      const data = stores?.data?.[storesType] || emptyArray
      const isLoading = stores?.isLoading || false
      const sortBy = stores?.sortBy
      const sortOrder = stores?.sortOrder
      const fetchTime = stores?.fetchTime
      const sortFunc = getCompareFunction(sortBy, sortOrder)
      const sortedData = data
        .sort(getCompareFunction(DEFAULT_SORT_BY.value, DEFAULT_SORT_BY.order))
        .map((store, index) => ({
          ...store,
          position: `${index + 1}. `,
        }))
        .sort(sortFunc)

      return {
        data: sortedData,
        errorCode: stores?.errorCode,
        fetchTime,
        isLoading,
        sortBy,
        sortOrder,
      }
    },
  )

export const selectCompanies = createSelector(
  selectRequestParams,
  (state: State) => state.companies,
  (requestParams, companies) => {
    const data = companies[getDateKey(requestParams)]?.data || emptyArray
    const isLoading = companies[getDateKey(requestParams)]?.isLoading || false

    return {
      isLoading,
      data,
    }
  },
)

export const selectExpanded = (state: State) => state.expanded

export const selectIsExpanded = ({ countryId, channel = '', owner = '', saleType = '', type }: Options) =>
  createSelector(selectChannelsType, selectExpanded, (channelType, expanded) => {
    let isAnyOpen

    switch (type) {
      case 'group':
        isAnyOpen = Boolean(expanded.group)
        break
      case 'channels':
        isAnyOpen = expanded.channels === channel
        break
      case 'owners':
        isAnyOpen = expanded.owners === owner
        break
      case 'saleTypes':
        isAnyOpen = expanded.saleTypes === saleType
        break
      case 'countries':
        if (expanded.saleTypes && expanded['countries-saleTypes']) {
          isAnyOpen = expanded['countries-saleTypes'] === countryId
        }

        if (expanded.channels && expanded['countries-channels']) {
          isAnyOpen = expanded['countries-channels'] === countryId
        }

        if (expanded.countries && expanded['countries-countries']) {
          isAnyOpen = expanded['countries-countries'] === countryId
        }

        if (expanded.owners && expanded['countries-owners']) {
          isAnyOpen = expanded['countries-owners'] === countryId
        }

        if (channelType === 'countries') {
          isAnyOpen = expanded.countries === countryId
        }

        break
    }

    return isAnyOpen || false
  })
